import { Card } from "../../../components/shadcn-ui/card";
import { LeadDetailsShimmer } from "./LeadDetailsShimmer";
import { Users, ArrowRight, Mail, MapPin, Phone, IndianRupee, Calendar, Percent, User } from "lucide-react";
import { Button } from "@components/shadcn-ui/button";
import { useNavigate } from "react-router-dom";
import { encodeURIComponent as encodeURI } from "@lib/utils";
import { useIsMobile } from "@customHooks/use-mobile";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@components/shadcn-ui/accordian";

export const LeadDetails = ({ isLoading, lead, groupMemberInfo }: any) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  if (isLoading) {
    return <LeadDetailsShimmer />;
  }

  const {
    firstName,
    lastName,
    phoneNumber: mobileNumber,
    email: emailAddress,
    source,
    address = {},
    loanRequirementDetails,
  } = lead || {};

  const DetailRow = ({ icon: Icon, label, value, className = "" }: any) => (
    <div className={`flex items-start gap-3 text-sm ${className}`}>
      {Icon && <Icon className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />}
      <div className="flex flex-col">
        <span className="text-gray-500 text-xs font-medium uppercase tracking-wide">{label}</span>
        <span className="text-gray-900 font-medium">{value || "-"}</span>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <Card className="overflow-hidden bg-white border-slate-200 shadow-sm">
        {/* Header Section - Always Visible */}
        <div className="p-5 border-b border-slate-100 bg-slate-50/50">
          <div className="flex justify-between items-start gap-4">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-xl">
                {firstName?.[0]}{lastName?.[0]}
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900 leading-tight">{firstName} {lastName}</h2>
                <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                  <Phone className="h-3 w-3" />
                  <span>{mobileNumber}</span>
                  {address?.city && (
                    <>
                      <span className="text-gray-300">•</span>
                      <MapPin className="h-3 w-3" />
                      <span>{address?.city}, {address?.state}</span>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Desktop Group Badge */}
            {!isMobile && groupMemberInfo && (
              <div
                className="flex items-center gap-2 bg-blue-50 text-blue-700 px-3 py-1.5 rounded-full text-sm font-medium cursor-pointer hover:bg-blue-100 transition-colors border border-blue-100"
                onClick={() => navigate(`/dashboard/groups/${encodeURI(groupMemberInfo.id)}?city=${encodeURI(groupMemberInfo.city)}`)}
              >
                <Users className="h-4 w-4" />
                <span>{groupMemberInfo.name}</span>
                <ArrowRight className="h-3 w-3 ml-1" />
              </div>
            )}
          </div>
        </div>

        {/* Content Section */}
        <div className="p-0">
          {isMobile ? (
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="personal-info" className="border-b px-5">
                <AccordionTrigger className="hover:no-underline py-4 text-sm font-semibold text-gray-700">
                  Lead Information
                </AccordionTrigger>
                <AccordionContent className="pb-4">
                  <div className="space-y-4">
                    <DetailRow icon={Mail} label="Email" value={emailAddress} />
                    <DetailRow icon={User} label="Channel" value={source?.channel} />
                    <DetailRow icon={User} label="Relationship Officer" value={source?.referral} />
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="address-info" className="border-b px-5">
                <AccordionTrigger className="hover:no-underline py-4 text-sm font-semibold text-gray-700">
                  Address Details
                </AccordionTrigger>
                <AccordionContent className="pb-4">
                  <div className="space-y-4">
                    <DetailRow icon={MapPin} label="Address Line 1" value={address?.addressLineOne} />
                    <DetailRow icon={MapPin} label="Address Line 2" value={address?.addressLineTwo} />
                    <DetailRow icon={MapPin} label="Postal Code" value={address?.postalCode} />
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="loan-info" className="px-5 border-none">
                <AccordionTrigger className="hover:no-underline py-4 text-sm font-semibold text-gray-700">
                  Loan Requirements
                </AccordionTrigger>
                <AccordionContent className="pb-4">
                  <div className="grid grid-cols-2 gap-4">
                    <DetailRow icon={IndianRupee} label="Amount" value={loanRequirementDetails?.amount} />
                    <DetailRow icon={Calendar} label="Tenure" value={`${loanRequirementDetails?.tenureInMonths || 0} Months`} />
                    <DetailRow icon={Percent} label="Interest Rate" value={loanRequirementDetails?.mobileNumber} /> {/* Note: Assuming field mapping is correct based on original code, though 'mobileNumber' for ROI seems odd */}
                  </div>
                </AccordionContent>
              </AccordionItem>

              {groupMemberInfo && (
                <div className="bg-blue-50/50 p-4 border-t border-blue-100">
                  <div className="flex items-center justify-between" onClick={() => navigate(`/dashboard/groups/${encodeURI(groupMemberInfo.id)}?city=${encodeURI(groupMemberInfo.city)}`)}>
                    <div className="flex items-center gap-3">
                      <Users className="h-5 w-5 text-blue-600" />
                      <div>
                        <p className="text-xs text-blue-600 font-bold uppercase">Member of</p>
                        <p className="text-blue-900 font-semibold">{groupMemberInfo.name}</p>
                      </div>
                    </div>
                    <ArrowRight className="h-4 w-4 text-blue-400" />
                  </div>
                </div>
              )}
            </Accordion>
          ) : (
            // Desktop View - Clean Grid
            <div className="p-6 grid grid-cols-3 gap-y-8 gap-x-6">
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-900 border-b pb-2 mb-3">Lead Information</h3>
                <DetailRow icon={Mail} label="Email" value={emailAddress} />
                <DetailRow icon={User} label="Channel" value={source?.channel} />
                <DetailRow icon={User} label="Relationship Officer" value={source?.referral} />
              </div>

              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-900 border-b pb-2 mb-3">Address Details</h3>
                <DetailRow icon={MapPin} label="Address Line 1" value={address?.addressLineOne} />
                <DetailRow icon={MapPin} label="Address Line 2" value={address?.addressLineTwo} />
                <DetailRow icon={MapPin} label="Postal Code" value={address?.postalCode} />
              </div>

              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-900 border-b pb-2 mb-3">Loan Requirements</h3>
                <div className="grid grid-cols-2 gap-4">
                  <DetailRow icon={IndianRupee} label="Amount" value={loanRequirementDetails?.amount} />
                  <DetailRow icon={Calendar} label="Tenure" value={`${loanRequirementDetails?.tenureInMonths || 0} Months`} />
                  <DetailRow icon={Percent} label="Interest Rate" value={loanRequirementDetails?.mobileNumber} />
                </div>
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};
